import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Modal,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  getReadableAnimalActivity,
  getReadableAnimalCorpulence,
  getReadableAnimalSize,
} from '../../../../domain/Animal/AnimalDisplay'
import {
  AnimalDto,
  AnimalFormAndActivityDto,
  AnimalFormAndActivityDtoActivity,
  AnimalFormAndActivityDtoCorpulence,
  AnimalFormAndActivityDtoSize,
  AnimalWeightDto,
} from '../../../../interactors/gen/backendClient'
import { LineChart, Line, Tooltip, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts'

import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { useFormExtended } from '../../../../utils/hooks/useFormExtended'
import { omitEmptyString } from '../../../../utils/omitEmptyString'
import { ControlledSelectField } from '../../../common/ControlledSelectField'
import { Grid } from '@mui/material'
import { AnimalCardSaveButton } from '../AnimalCardSaveButton'
import { AnimalHealthWeight, NewAnimalHealthWeightModal } from './AnimalHealthWeight'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import dayjs from 'dayjs'
import { colors } from '../../../theme'
import { surfaceCardSx } from '../../../common/surfaceCardSx'

interface Props {
  animal: AnimalDto
  embedded?: boolean
}

type FormParams = {
  size: AnimalFormAndActivityDtoSize | ''
  corpulence: AnimalFormAndActivityDtoCorpulence | ''
  activity: AnimalFormAndActivityDtoActivity | ''
}

export const AnimalHealthFormAndActivityCard: React.FC<Props> = ({ animal, embedded = false }) => {
  const [weights, setWeights] = useState<AnimalWeightDto[]>(animal?.formAndActivity?.weight || [])
  const isDesktop = useMediaQuery('(min-width:1100px)')
  const [isExpanded, setIsExpanded] = useState(isDesktop)

  const canEditAnimal = useCanEditAnimal()

  const editAnimalMutation = useEditAnimalMutation({})

  const getDefaultValues = (animal: AnimalDto): FormParams => ({
    ...animal.formAndActivity,
    size: animal.formAndActivity?.size || '',
    corpulence: animal.formAndActivity?.corpulence || '',
    activity: animal.formAndActivity?.activity || '',
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useFormExtended(animal, getDefaultValues)

  const fieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: { xs: '0.78rem', sm: '0.82rem' },
      fontWeight: 400,
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      fontSize: { xs: '0.72rem', sm: '0.76rem' },
      fontWeight: 400,
    },
    '& .MuiOutlinedInput-input, & .MuiSelect-select': {
      fontSize: { xs: '0.92rem', sm: '0.96rem' },
      fontWeight: 400,
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: { xs: '0.92rem', sm: '0.96rem' },
      fontWeight: 300,
      opacity: 1,
    },
  }

  const onSubmit = async (data: FormParams) => {
    if (!isDirty) return
    const formAndActivity: AnimalFormAndActivityDto = {
      size: data.size || undefined,
      corpulence: data.corpulence || undefined,
      activity: data.activity || undefined,
    }

    const update = omitEmptyString({
      ...animal,
      formAndActivity,
    })

    await editAnimalMutation.mutate(update)
  }

  const initialWeightsRef = useRef(animal?.formAndActivity?.weight)

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return // don't run the effect
    }

    const saveWeights = async () => {
      if (JSON.stringify(weights) !== JSON.stringify(initialWeightsRef.current)) {
        const formAndActivity: AnimalFormAndActivityDto = {
          ...animal.formAndActivity,
          weight: weights || undefined,
        }

        const update = omitEmptyString({
          ...animal,
          formAndActivity,
        })

        await editAnimalMutation.mutate(update)
      }
    }

    saveWeights()
  }, [weights])

  useEffect(() => {
    setIsExpanded(isDesktop)
  }, [isDesktop])

  const sortedWeights = [...weights].sort((a, b) => {
    const aDate = a.dateOfWeightIn ? new Date(a.dateOfWeightIn) : new Date(0)
    const bDate = b.dateOfWeightIn ? new Date(b.dateOfWeightIn) : new Date(0)

    return bDate.getTime() - aDate.getTime()
  })

  const content = (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {embedded ? null : (
        <CardHeader
          sx={{
            '& .MuiCardHeader-action': {
              alignSelf: 'center',
              mt: 0,
              mb: 0,
            },
          }}
          title="💉 Forme & activité"
          titleTypographyProps={{ sx: { fontSize: { xs: 15.5, sm: 16 }, fontWeight: 700, lineHeight: 1.2 } }}
          action={
            !isDesktop ? (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setIsExpanded((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setIsExpanded((prev) => !prev)
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  cursor: 'pointer',
                  color: colors.cello,
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 800, lineHeight: 1.2 }}>
                  Consulter
                </Typography>
                <Box sx={{ ml: -0.25, display: 'flex', alignItems: 'center' }}>
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </Box>
            ) : null
          }
        />
      )}
      <Collapse in={embedded ? true : isExpanded} timeout="auto" unmountOnExit={!isDesktop && !embedded}>
        <CardContent
          sx={{
            height: '100%',
            pb: embedded ? '0 !important' : '16px !important',
            px: embedded ? 0 : undefined,
            pt: embedded ? 0 : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: { xs: 1.5, sm: 2 },
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={4} md={12}>
                  <ControlledSelectField
                    sx={fieldSx}
                    control={control}
                    error={errors.size}
                    fieldName="size"
                    size="small"
                    label="Taille"
                    options={Object.values(AnimalFormAndActivityDtoSize).map((field) => ({
                      label: getReadableAnimalSize(field),
                      value: field,
                    }))}
                    requiredRule={undefined}
                  />
                </Grid>
                <Grid item xs={4} md={12}>
                  <ControlledSelectField
                    sx={fieldSx}
                    control={control}
                    error={errors.corpulence}
                    fieldName="corpulence"
                    size="small"
                    label="Corpulence"
                    options={Object.values(AnimalFormAndActivityDtoCorpulence).map((field) => ({
                      label: getReadableAnimalCorpulence(field),
                      value: field,
                    }))}
                    requiredRule={undefined}
                  />
                </Grid>
                <Grid item xs={4} md={12}>
                  <ControlledSelectField
                    sx={fieldSx}
                    control={control}
                    error={errors.activity}
                    fieldName="activity"
                    size="small"
                    label="Activité"
                    options={Object.values(AnimalFormAndActivityDtoActivity).map((field) => ({
                      label: getReadableAnimalActivity(field),
                      value: field,
                    }))}
                    requiredRule={undefined}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <AnimalCardSaveButton
                  isLoading={editAnimalMutation.isLoading}
                  disabled={!isDirty}
                  sx={{
                    borderRadius: '999px',
                    minHeight: 36,
                    px: 2.25,
                    fontWeight: 800,
                    fontSize: 13,
                    width: { xs: '100%', sm: 'auto' },
                    backgroundColor: 'rgba(255, 255, 255, 0.72)',
                    borderColor: 'rgba(122, 129, 151, 0.24)',
                    color: colors.cello,
                    '&:hover': { backgroundColor: 'white', borderColor: 'rgba(122, 129, 151, 0.35)' },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
              {weights.length > 0 ? (
                <>
                  {/* Preview chart (opens full history modal) */}
                  <WeightHistoryPopUp
                    weights={sortedWeights}
                    setWeights={setWeights}
                    disabled={!canEditAnimal}
                    trigger="chart"
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.9rem', sm: '0.98rem' },
                        fontWeight: 900,
                        color: colors.cello,
                        lineHeight: 1.2,
                      }}
                    >
                      Poids actuel
                    </Typography>
                    <NewAnimalHealthWeightModal
                      weights={weights}
                      setWeights={setWeights}
                      disabled={!canEditAnimal}
                      iconOnly
                      buttonText=""
                      buttonSx={{
                        width: 44,
                        height: 44,
                        minWidth: 44,
                        borderRadius: '999px',
                        backgroundColor: '#FCB402',
                        color: 'white',
                        boxShadow: '0 8px 18px rgba(32, 56, 100, 0.18)',
                        '&:hover': { backgroundColor: '#E9A900' },
                      }}
                    />
                  </Box>

                  {/* Only show the last recorded weight in the animal sheet */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 3,
                      border: '1px solid rgba(228, 232, 242, 0.9)',
                      backgroundColor: '#F9FBFF',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.83rem', sm: '0.88rem' },
                        fontWeight: 700,
                        color: colors.cello,
                        lineHeight: 1.2,
                      }}
                    >
                      {sortedWeights[0]?.dateOfWeightIn
                        ? dayjs(sortedWeights[0].dateOfWeightIn).format('DD/MM/YYYY')
                        : '..'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.83rem', sm: '0.88rem' },
                        fontWeight: 900,
                        color: colors.cello,
                        lineHeight: 1.2,
                      }}
                    >
                      {sortedWeights[0]?.weightInKgs || '..'} kg
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                    <WeightHistoryPopUp
                      weights={sortedWeights}
                      setWeights={setWeights}
                      disabled={!canEditAnimal}
                      trigger="link"
                    />
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.9rem', sm: '0.98rem' },
                      fontWeight: 900,
                      color: colors.cello,
                      mt: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    Suivi de poids
                  </Typography>
                  <NewAnimalHealthWeightModal
                    weights={weights}
                    setWeights={setWeights}
                    firstWeightIn={true}
                    disabled={!canEditAnimal}
                  />
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Collapse>
    </form>
  )

  return embedded ? (
    <Box sx={{ height: '100%' }}>{content}</Box>
  ) : (
    <Card sx={{ ...surfaceCardSx, height: '100%' }}>{content}</Card>
  )
}

interface PopUpProps {
  weights: AnimalWeightDto[]
  setWeights: (weights: Array<AnimalWeightDto>) => void
  disabled?: boolean
  trigger?: 'link' | 'chart'
}

const WeightHistoryPopUp: React.FC<PopUpProps> = ({ weights, setWeights, disabled = false, trigger = 'link' }) => {
  const [open, setOpen] = React.useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  // parse and sort data by date, then store a numeric timestamp for the chart
  const processedData = React.useMemo(() => {
    return weights
      .slice()
      .sort((a, b) => dayjs(a.dateOfWeightIn).valueOf() - dayjs(b.dateOfWeightIn).valueOf())
      .map((w) => ({
        ...w,
        dateTimestamp: dayjs(w.dateOfWeightIn).valueOf(), // numeric
      }))
  }, [weights])

  const style: SxProps = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 550,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: { sm: 4, xs: 2 },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxHeight: '80%',
    maxWidth: { sm: '80%', xs: '95%' },
    overflowY: 'auto',
  }

  return (
    <>
      {trigger === 'chart' ? (
        <Box
          role="button"
          tabIndex={0}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleOpen()
          }}
          sx={{
            width: '100%',
            height: 140,
            borderRadius: 4,
            border: '1px solid rgba(228, 232, 242, 0.9)',
            backgroundColor: '#F9FBFF',
            overflow: 'hidden',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#F3F6FC' },
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={processedData} margin={{ top: 16, right: 18, left: 10, bottom: 8 }}>
              <CartesianGrid strokeDasharray="4 4" stroke="rgba(23, 31, 52, 0.08)" />
              <XAxis
                dataKey="dateTimestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(val) => dayjs(val).format('DD/MM')}
                tick={{ fontSize: 11, fill: '#7A8197' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 11, fill: '#7A8197' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip labelFormatter={(value) => dayjs(value).format('DD/MM/YYYY')} />
              <Line
                type="monotone"
                dataKey="weightInKgs"
                name="Poids (kg)"
                stroke="#698DAC"
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Button
          variant="text"
          onClick={handleOpen}
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 700,
            color: '#7A8197',
            '&:hover': { color: '#171F34' },
          }}
        >
          Historique
        </Button>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...style, borderRadius: 4, border: '1px solid rgba(228, 232, 242, 0.9)' }}>
          {/* TITLE */}
          <Box sx={{ width: '100%', mb: 2 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: colors.cello, textAlign: 'left' }}>
              Historique du poids
            </Typography>
          </Box>

          {/* CHART CONTAINER */}
          <Box sx={{ width: '100%', height: 300, mb: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={processedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(23, 31, 52, 0.08)" />
                <XAxis
                  dataKey="dateTimestamp"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(val) => dayjs(val).format('DD/MM')}
                  tick={{ fontSize: 11, fill: '#7A8197' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11, fill: '#7A8197' }} axisLine={false} tickLine={false} width={30} />
                <Tooltip labelFormatter={(value) => dayjs(value).format('DD/MM/YYYY')} />
                <Line
                  type="monotone"
                  dataKey="weightInKgs"
                  name="Poids (kg)"
                  stroke="#698DAC"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* LIST OF WEIGHTS */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              alignItems: 'left',
              gap: 2,
              maxHeight: 240,
              overflowY: 'auto',
              pt: 2,
            }}
          >
            {weights.map((weight, index) => (
              <AnimalHealthWeight
                key={index}
                weight={weight}
                weights={weights}
                setWeights={setWeights}
                flexDirection="row"
                disabled={disabled}
              />
            ))}
          </Box>
        </Box>
      </Modal>
    </>
  )
}
